import { Formik, FormikValues } from 'formik';
import React, { ChangeEvent, useContext, useState } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from './styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { getFileExtension } from '../../../utils/s3/utils';
import { PUBLIC_BUCKET } from '../../../defines';
import { uploadPublicFile } from '../../../utils/s3';
import { getAuthorUploadFileName } from '../../../api/author/utils';
import { ReactComponent as AccessLinkIcon } from '../../../assets/icons/icon-grey-check-square-offset.svg';
import ProjectTextArea from '../../inputs/ProjectTextArea';
import { Promotion } from '../../../api/types/contentManagement';
import { updatePromotion } from '../../../api/contentManagement';
import { createPromotion } from '../../../api/contentManagement';
import { CreatePromotionDto } from '../../../api/typeDtos/contentManagement';
import { MinimalPromotionSchema } from '../../../api/contentManagement/parser';
import { textFieldUtils } from '../../../utils/textFields';
import ProjectCalendarPicker from '../../dateAndTime/ProjectCalendarPicker';
import { getPastMidnightUTC } from '../../../utils/dateFormat';

const initialValues = {
  offerId: '',
  title: '',
  description: '',
  imagePath: '',
  buttonText: '',
  timer: undefined,
  startDate: undefined,
  endDate: undefined,
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: Promotion;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);

  const [imagePath, setImagePath] = useState<string | undefined>(modalState?.imagePath || undefined);
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [imageUploadProgress, setImageUploadProgress] = useState<number | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | null>(
    modalState?.startDate ? new Date(modalState?.startDate) : null,
  );
  const [endDate, setEndDate] = useState<Date | null>(modalState?.endDate ? new Date(modalState?.endDate) : null);

  const promotionInitialValues = {
    offerId: modalState?.offerId,
    title: modalState?.title,
    startDate: modalState?.startDate,
    endDate: modalState?.endDate,
    description: modalState?.description,
    imagePath: modalState?.imagePath,
    timer: modalState?.timer,
    buttonText: modalState?.buttonText,
  };

  const _onSubmit = async (values: FormikValues) => {
    if (!startDate || !endDate) {
      return;
    }

    await onSave?.({
      ...values,
      imagePath: imagePath,
      startDate: startDate.getTime(),
      endDate: endDate.getTime(),
    });
  };

  const onUploadPublicFile = async (e: ChangeEvent<HTMLInputElement> | undefined, fileName: string, path: string) => {
    setIsLoading(true);
    const file = e?.target?.files?.[0];
    // @ts-ignore
    const newFile = new File([file], fileName, { type: file?.type });
    if (newFile) {
      if (e && e.target && e.target.value) {
        e.target.value = '';
      }

      await uploadPublicFile(newFile, path, (progress) => {
        setImageUploadProgress(progress);
        if (progress === 100) {
          setImagePath(path);
          const link = PUBLIC_BUCKET + path;
          setImageUrl(link);
          setImageUploadProgress(undefined);
        }
      });
    }
    setIsLoading(false);
  };

  const selectHandlerForImageFile = async (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
    const file = event?.target?.files?.[0];

    if (file) {
      const extension = getFileExtension(file);
      const fileName = getAuthorUploadFileName();
      const path = 'images/'.concat(fileName).concat('.', extension || '');
      await onUploadPublicFile(event, fileName, path);
    }
  };

  const onAccessFileLink = () => {
    const _imageUrl = imageUrl || PUBLIC_BUCKET + imagePath;
    window.open(_imageUrl, '_blank');
  };

  const onClearImageLink = () => {
    setImagePath(undefined);
  };

  const onSelectStartDateFromCalendar = (value: Date | null) => {
    if (value !== null) {
      const utcDate = new Date(getPastMidnightUTC(value));
      setStartDate(utcDate);
    }
  };

  const onSelectEndDateFromCalendar = (value: Date | null) => {
    if (value !== null) {
      const utcDate = new Date(getPastMidnightUTC(value));
      setEndDate(utcDate);
    }
  };

  const onClearStartDate = () => {
    setStartDate(null);
  };

  const onClearEndDate = () => {
    setEndDate(null);
  };

  return (
    <Formik
      initialValues={modalState ? promotionInitialValues : initialValues}
      validationSchema={MinimalPromotionSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched, setFieldValue }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Title</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.title}
                    onChangeText={handleChange}
                    name={'title'}
                    errorMessage={errors.title && touched.title ? errors?.title : undefined}
                    placeholder={'Enter promotion title'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Offer ID</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.offerId}
                    onChangeText={handleChange}
                    name={'offerId'}
                    errorMessage={errors.offerId && touched.offerId ? errors?.offerId : undefined}
                    placeholder={'Enter RevenueCat offer ID'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.DateFieldsWrapper>
                  <Components.DateFieldWrapper>
                    <Components.FieldLabel>Start date</Components.FieldLabel>
                    <ProjectCalendarPicker
                      dateValue={values?.startDate ? new Date(values?.startDate) : null}
                      onDateValueChange={(value) => {
                        onSelectStartDateFromCalendar(value);
                        setFieldValue('startDate', value?.getTime());
                      }}
                      valueDisplayFormat={'day'}
                      maxDate={endDate || undefined}
                    />
                  </Components.DateFieldWrapper>
                  {startDate && (
                    <Components.ClearButton
                      onClick={() => {
                        onClearStartDate();
                        setFieldValue('startDate', undefined);
                      }}>
                      Clear start date
                    </Components.ClearButton>
                  )}
                  {errors.startDate && touched.startDate && (
                    <Components.ErrorMessage>{'Start date is required'}</Components.ErrorMessage>
                  )}
                </Components.DateFieldsWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.DateFieldsWrapper>
                  <Components.DateFieldWrapper>
                    <Components.FieldLabel>End date</Components.FieldLabel>
                    <ProjectCalendarPicker
                      dateValue={values?.endDate ? new Date(values?.endDate) : null}
                      onDateValueChange={(value) => {
                        onSelectEndDateFromCalendar(value);
                        setFieldValue('endDate', value?.getTime());
                      }}
                      valueDisplayFormat={'day'}
                      minDate={startDate || undefined}
                    />
                  </Components.DateFieldWrapper>
                  {endDate && (
                    <Components.ClearButton
                      onClick={() => {
                        onClearEndDate();
                        setFieldValue('endDate', undefined);
                      }}>
                      Clear end date
                    </Components.ClearButton>
                  )}
                  {errors.endDate && touched.endDate && (
                    <Components.ErrorMessage>{'End date is required'}</Components.ErrorMessage>
                  )}
                </Components.DateFieldsWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Timer</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.timer ? values?.timer.toString() : ''}
                    onChangeText={handleChange}
                    name={'timer'}
                    errorMessage={errors.timer && touched.timer ? errors?.timer : undefined}
                    placeholder={'Enter timer in minutes'}
                    {...textFieldUtils.inputPositiveNumber}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Image</Components.FieldLabel>
                  <Components.UploadFileWrapper>
                    <Components.InputMenu
                      type='file'
                      accept={'image/*'}
                      id='imageFile'
                      onChange={selectHandlerForImageFile}
                    />
                    <ProjectTextField value={imagePath || ''} disabled placeholder={'No image path'} />
                    {imagePath ? (
                      <>
                        <Components.IconWrapper onClick={() => onAccessFileLink()}>
                          <AccessLinkIcon />
                        </Components.IconWrapper>
                        <Components.ClearButton onClick={onClearImageLink}>Clear</Components.ClearButton>
                      </>
                    ) : (
                      <Components.UploadButton htmlFor={'imageFile'}>
                        {typeof imageUploadProgress === 'number' ? `${imageUploadProgress}%` : 'Upload Image'}
                      </Components.UploadButton>
                    )}
                  </Components.UploadFileWrapper>
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Button text</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.buttonText}
                    onChangeText={handleChange}
                    name={'buttonText'}
                    errorMessage={errors.buttonText && touched.buttonText ? errors?.buttonText : undefined}
                    placeholder={'Enter text for the offer button'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Description</Components.FieldLabel>
                  <ProjectTextArea
                    value={values.description}
                    onChangeText={handleChange}
                    name={'description'}
                    errorMessage={errors.description && touched.description ? errors?.description : undefined}
                    placeholder={`Enter promotion description`}
                    rows={10}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const CreateOrEditPromotionModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { promotion?: Promotion };
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data: CreatePromotionDto = {
      offerId: values?.offerId,
      title: values?.title,
      startDate: values?.startDate,
      endDate: values?.endDate,
      description: values?.description?.length ? values?.description : null,
      imagePath: values?.imagePath || null,
      timer: values?.timer || null,
      buttonText: values?.buttonText || null,
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.promotion?.id
        ? await updatePromotion({ ...data, id: modalState?.promotion?.id })
        : await createPromotion(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.promotion?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a promotion');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.promotion} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.promotion ? `Edit promotion` : 'Create new promotion'}
        titleSuffix={modalState?.promotion ? `(ID: ${modalState?.promotion?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditPromotionModal;
